import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

/**
 * FeaturesContext — exposes the deployment-wide set of active feature keys
 * controlled by the Super Admin (via plan + add-ons). Used everywhere in
 * the app (sidebar, storefront nav, tabs) to conditionally render modules.
 *
 * Always refetches on mount + whenever `refresh()` is called (e.g. after
 * the tenant enables/disables a feature).
 */
const FeaturesCtx = createContext({
  features: new Set(),
  loading: true,
  has: () => false,
  refresh: async () => {},
});

export function FeaturesProvider({ children }) {
  const [features, setFeatures] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/features/enabled");
      setFeatures(new Set(data.feature_keys || []));
    } catch {
      setFeatures(new Set());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const has = (key) => features.has(key);

  return (
    <FeaturesCtx.Provider value={{ features, loading, has, refresh }}>
      {children}
    </FeaturesCtx.Provider>
  );
}

export function useFeatures() {
  return useContext(FeaturesCtx);
}
